import { Injectable } from '@angular/core';
import { FragQueryService } from 'src/injectables/frag-query.service';
import { cleanFilename, downloadContent } from '@lib/dom/download';
import { MeasurementTypeToUnit } from '@lib/angular/measurement/measurement-type-to-unit';
import { measurement } from '@lib/measurement';
import { ExtendedTypeMeta } from '@lib/measurement/types';
import { DialogService } from '@app/old-ui/dialog/dialog.service';
import { firstValueFrom } from 'rxjs';
import { fragQuery, muckpileQuery, User } from '@models';
import { WebClient } from './web-client/web-client';
import { environment } from '@environment';

export interface MonthlyReportMonthOption {
    month: string;
    label: string;
    status: string;
    generatedAt?: string;
    end?: number;
}

@Injectable({
    providedIn: 'root',
})
export class CsvUtilService {
    constructor(
        private readonly fragQuery: FragQueryService,
        private dialogService: DialogService,
        private readonly webClient: WebClient,
        private readonly user: User
    ) { }

    async getMonthlyReportMonths(deviceId: number) {
        const response = await this.webClient.rest({
            route: 'monthly-fragmentation-report/months',
            method: 'get',
            body: {
                email: this.user.email,
                authToken: this.user.authToken,
                deviceId,
            },
        });

        return response.toRecords<MonthlyReportMonthOption>() ?? [];
    }

    async downloadMonthlyReportCsv(deviceId: number, deviceName: string, month: string) {
        const weightType = await firstValueFrom(this.fragQuery.weightType$);
        const percentsMap = await firstValueFrom(this.fragQuery.newPercents$);
        const percents = Array.from(new Set([0, ...percentsMap.keys()]))
            .sort((a, b) => a - b);
        const params = new URLSearchParams({
            email: this.user.email,
            authToken: this.user.authToken,
            deviceId: String(deviceId),
            month,
            weightType,
            percents: percents.join(','),
        });

        const url = `${environment.backendUrl}/monthly-fragmentation-report/csv?${params.toString()}`;
        const response = await fetch(url);
        if (!response.ok) {
            throw new Error('Monthly report is not ready');
        }

        const csvContent = await response.text();
        downloadContent(
            cleanFilename(`${deviceName}_monthly_report_${month}`, 'csv'),
            csvContent,
            'text/csv'
        );
        this.dialogService.closeAll();
    }

    async downloadCsv(
        fileType: 'single-row' | 'multiple-row',
        particleSizeUnit: string,
        weightTypeTitle: string,
        totalPayload: number,
        deviceName: string,
        psdDataFormatInCSV: 'Default' | 'Single Row' | 'Multiple Rows',
        selectedDValues?: string[]
    ) {
        const dateTimeFormat: Intl.DateTimeFormatOptions = {
            year: '2-digit',
            month: '2-digit',
            day: '2-digit',
            hour: '2-digit',
            hourCycle: 'h23',
            minute: '2-digit',
            second: '2-digit',
        };

        let input: fragQuery.timeline.sieveSizesAtPercents.Response = null;

        this.fragQuery.timelineSieveSizesAtPercents$.subscribe((data) => {
            if (data) {
                input = data;
            }
        })

        if (!input || !input.intervals || !input.percents) {
            console.error('Invalid input data');
            return;
        } else {

            const percents = input.percents.slice().sort((a: number, b: number) => a - b);
            const tsrows: string[] = [];
            const particleSizes = input.summary.psd.length;

            const device = await firstValueFrom(this.fragQuery.device$);
            const customSizes = device?.timelineCustomSizes ? JSON.parse(device.timelineCustomSizes) : [];

            for (let i = 0; i < particleSizes; i++) {
                tsrows.push(`TS${i + 1}`);
                tsrows.push(`PP${i + 1}`);
            }

            const unitSystem = particleSizeUnit as keyof ExtendedTypeMeta;
            const measurementTypeToUnit = new MeasurementTypeToUnit();
            const unit = measurementTypeToUnit.getUnitsForSystem(unitSystem).particleSize;

            const customSizeHeaders = customSizes.map(
                (size: any) => `${measurement.convert(size.size, 'meter', unit).toFixed(2)}${measurement.abbreviation(unit)}`
            );

            const customSizePercentsWithUnit = Object.values(
                input.summary?.customSizePercents ?? []
            ).map((value) => `${value}%`);

            let csvRows: (string | number)[][] = [];

            const payloadUnitAbbr = await firstValueFrom(this.fragQuery.payloadUnitAbbreviation$);
            const totalPayloadHeader = `Total Payload (${payloadUnitAbbr})`;

            if (psdDataFormatInCSV === 'Single Row') {
                csvRows = [
                    [
                        'Begin Time',
                        'End Time',
                        'Total Haulers',
                        totalPayloadHeader,
                        weightTypeTitle,
                        ...selectedDValues.map((v: string) => `D${v}`),
                        ...tsrows,
                        ...customSizeHeaders,
                    ],
                ];

                const psdData: number[] = [];
                input.summary.psd.forEach((psd: any, index: number) => {
                    const passing_percent = psd.passing_percent ?? (index > 0 ? input.summary.psd[index - 1].passing_percent : 0);
                    psd.passing_percent = passing_percent;
                    psdData.push(parseFloat(psd.size.toFixed(2)));
                    psdData.push(parseFloat(passing_percent.toFixed(2)));
                });

                csvRows.push([
                    input.begin.toLocaleString(undefined, dateTimeFormat).replace(',', ''),
                    input.end.toLocaleString(undefined, dateTimeFormat).replace(',', ''),
                    input.intervals.length,
                    totalPayload.toFixed(2),
                    input.summary.totalWeight.toFixed(2),
                    ...selectedDValues.map((value: string) => input.summary.dvalues[value].toFixed(2)),
                    ...psdData,
                    ...customSizePercentsWithUnit ?? [],
                ]);

            } else if (psdDataFormatInCSV === 'Multiple Rows') {
                csvRows = []
                csvRows.push(
                    ['Begin Time', input.begin.toLocaleString(undefined, dateTimeFormat).replace(',', ''), '', ''],
                    ['End Time', input.end.toLocaleString(undefined, dateTimeFormat).replace(',', ''), '', ''],
                    ['Total Haulers', input.intervals.length, '', ''],
                    [totalPayloadHeader, totalPayload.toFixed(2), '', ''],
                    [weightTypeTitle, input.summary.totalWeight.toFixed(2), '', '']
                );

                csvRows.push(['', '', '', '']);
                selectedDValues.forEach((v: string, index: number) => {
                    csvRows.push(
                        [`D${v}`, input.summary.dvalues[v]],
                    );
                })

                csvRows.push(['', '', '', '']);
                csvRows.push(['Sr. Number', `Size (${particleSizeUnit})`, 'Target Interval %', 'Cumulative %'])

                input.summary.psd.forEach((psd: any, i: number, psdArray: any[]) => {
                    const targetIntervalPercent = i ? parseFloat((psd.passing_percent - psdArray[i - 1].passing_percent).toFixed(2)) : parseFloat(psd.passing_percent.toFixed(2));

                    csvRows.push([
                        i + 1,
                        psd.size.toFixed(2),
                        targetIntervalPercent.toFixed(2),
                        psd.passing_percent.toFixed(2),
                    ]);
                });

                csvRows.push(['', '', '', '']);
                customSizes.forEach((size: any, index: number) => {
                    csvRows.push([
                        `${measurement
                            .convert(size.size, 'meter', unit)
                            .toFixed(2)}${measurement.abbreviation(unit)}`,
                        `${Object.values(input.summary.customSizePercents)[index].toFixed(2)}%`,
                    ]);
                });
            } else {
                csvRows = [
                    [
                        'Begin Time',
                        'End Time',
                        ...percents.map((v: number) => `D${v}`),
                        ...tsrows,
                        ...customSizeHeaders,
                    ],
                ];

                input.intervals.forEach((interval: any) => {
                    const psdData: number[] = [];
                    let maxPassingPercent = 0;

                    interval.psd.forEach((psd: any, index: number) => {
                        let passing_percent = psd.passing_percent ?? (index > 0 ? interval.psd[index - 1].passing_percent : 0);
                        passing_percent = Math.max(passing_percent, maxPassingPercent);

                        psd.passing_percent = passing_percent;
                        maxPassingPercent = passing_percent;

                        psdData.push(parseFloat(psd.size.toFixed(2)));
                        psdData.push(parseFloat(passing_percent.toFixed(2)));
                    });

                    csvRows.push([
                        interval.begin.toLocaleString(undefined, dateTimeFormat).replace(',', ''),
                        interval.end.toLocaleString(undefined, dateTimeFormat).replace(',', ''),
                        ...interval.data.map((v: any) => (typeof v === 'number' ? v.toFixed(3) : '')),
                        ...psdData,
                        ...(interval.customSizePercents ?? []).map((value: number) => `${value}%`),
                    ]);
                });
            }

            const csvContent = csvRows.map((row) => row.join(',')).join('\n');
            downloadContent(
                cleanFilename(`${deviceName}_report_${new Date().toISOString()}_${psdDataFormatInCSV}`, 'csv'),
                csvContent
            );
        }
        this.dialogService.closeAll()
    }

    async downloadMuckpileCsv(taggedData: muckpileQuery.details.Response, muckpile: muckpileQuery.list.MuckpileInfo) {
        if (!taggedData) {
            console.error('Invalid taggede data', taggedData);
            return;
        }

        let csvRows: (string | number)[][] = [];
        const percents = [10, 20, 30, 40, 50, 60, 70, 80, 90, 100];
        const tsrows: string[] = [];
        const particleSizes = taggedData.actual_fragmentation.psd.length;

        for (let i = 0; i < particleSizes; i++) {
            tsrows.push(`TS${i + 1}`);
            tsrows.push(`PP${i + 1}`);
        }

        csvRows = [
            [
                ...percents.map((v: number) => `D${v}`),
                ...tsrows,
            ],
        ];

        const psdData: number[] = [];
        let maxPassingPercent = 0;

        taggedData.actual_fragmentation.psd.forEach((psd: { size: number, passing_percent: number }, index: number, arr: any) => {
            let passing_percent =
                psd.passing_percent ??
                (index > 0 ? arr[index - 1].passing_percent : 0);

            passing_percent = Math.max(passing_percent, maxPassingPercent);
            maxPassingPercent = passing_percent;

            psdData.push(parseFloat(psd.size.toFixed(2)));
            psdData.push(parseFloat(passing_percent.toFixed(2)));
        });

        csvRows.push([
            ...taggedData.actual_fragmentation.dxx.map((v: any) => (typeof v === 'number' ? v.toFixed(3) : '')),
            ...psdData,
        ]);

        const csvContent = csvRows.map((row) => row.join(',')).join('\n');

        downloadContent(
            cleanFilename(`${muckpile?.name ?? ""}_muckpile_report_${new Date().toISOString()}`, 'csv'),
            csvContent
        );

        this.dialogService.closeAll()
    }
}
